/**
 * Categorías de activos en bóveda: valor único en BD y contrato API (slug en inglés).
 * La etiqueta visible es siempre vía i18n (`dashboardUi.vault.categories.<slug>`).
 */
export const VAULT_ASSET_TYPE_SLUGS = [
  "appliances",
  "electronics",
  "furniture",
  "vehicles",
  "other",
] as const;

export type VaultAssetTypeSlug = (typeof VAULT_ASSET_TYPE_SLUGS)[number];

const SLUG_SET: ReadonlySet<string> = new Set(VAULT_ASSET_TYPE_SLUGS);

export function isVaultAssetTypeSlug(s: string): s is VaultAssetTypeSlug {
  return SLUG_SET.has(s);
}

function foldLabel(s: string): string {
  return s
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{M}+/gu, "");
}

/** Variantes de UI / datos legados → slug canónico (clave ya normalizada con foldLabel). */
const FOLDED_ALIAS_TO_SLUG: Record<string, VaultAssetTypeSlug> = {
  appliances: "appliances",
  electronics: "electronics",
  furniture: "furniture",
  vehicles: "vehicles",
  other: "other",
  appliance: "appliances",
  electronic: "electronics",
  vehicle: "vehicles",
  others: "other",
  electrodomesticos: "appliances",
  electrodomestico: "appliances",
  electronica: "electronics",
  muebles: "furniture",
  vehiculos: "vehicles",
  otros: "other",
};

/**
 * Convierte entrada de API (slug o alias legado) al slug guardado en BD, o null si vacío.
 * @returns `invalid` si hay texto no reconocido.
 */
export function parseVaultAssetTypeForStorage(
  raw: string | null | undefined,
): { ok: true; slug: VaultAssetTypeSlug | null } | { ok: false } {
  if (raw === null || raw === undefined) {
    return { ok: true, slug: null };
  }
  const t = raw.trim();
  if (t === "") {
    return { ok: true, slug: null };
  }
  const folded = foldLabel(t);
  if (isVaultAssetTypeSlug(folded)) {
    return { ok: true, slug: folded };
  }
  const mapped = FOLDED_ALIAS_TO_SLUG[folded];
  if (mapped) {
    return { ok: true, slug: mapped };
  }
  return { ok: false };
}

/**
 * Clave de agrupación en listas: slug canónico, `__none__` si vacío, o texto libre si no mapea (datos huérfanos post-migración).
 */
export function vaultAssetTypeSectionKey(raw: string | null | undefined): string {
  const parsed = parseVaultAssetTypeForStorage(raw);
  if (parsed.ok) {
    return parsed.slug ?? "__none__";
  }
  const t = raw?.trim();
  return t && t.length > 0 ? t : "__none__";
}
